import type { OpenedChest } from "@/components/Event/EventMarketBox.vue";
import type SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import { type ResourceType } from "@/game/entities/config/resources";
import type AbstractPoiTileEntity from "@/game/entities/poi/AbstractPoiTileEntity";
import useSocketService from "@/hooks/useSocketService";
import {
	OfferAnalyticsType,
	type OfferAnalyticsClickSource,
} from "@/libs/SocketService";
import type { ExtendedPiggyBankOffer } from "@/services/OfferProvider";
import type { QuestActive } from "@/types/quest";
import type { ShopItem } from "@/types/shop";
import type { TileCoordinates } from "@/types/tile";
import type { News, StatusTypes } from "@/types/ui";
import { SkinTypes } from "@/types/ui";
import {
	createImmutableState,
	getImmutableStateProperty,
	setImmutableStateProperty,
} from "@/utils/state";
import type { ChronicleData } from "@/views/Quests/QuestsView.vue";
import { defineStore } from "pinia";
import {
	computed,
	markRaw,
	shallowRef,
	type Component,
	type ComputedRef,
	type Ref,
} from "vue";
import { type MapExpansionConfig } from "./useMapExpansionStore";

export enum PopupType {
	INFO = "info",
	ALERT = "alert",
	ERROR = "error",
	CHEST = "chest",
	ACCOUNT_LINK_OPTIONS = "acc_link_options",
	RATE_US = "rate_us",
	DELETE = "delete",
	NEWS = "news",
	DAILY_REWARD = "daily_reward",
	CHANGE_NAME = "change_name",
	REWARDED_AD = "rewarded_ad",
	QT = "qt",
	UPGRADE = "upgrade",
	EVENT = "event",
	DISCORD_REWARD = "discord_reward",
	LOCKED_MAP_REGION = "locked_map_region",
	RULES = "rules",
	EVENT_POSSIBLE_REWARDS = "event_possible_rewards",
	NO_DIVIDER = "no_divider",
}

export interface PopupAction {
	label: string;
	buttonType?: StatusTypes;
	price?: { type: ResourceType; value: number };
	iconSize?: number;
	callback?: () => void;
}

export interface OfferAnalytics {
	clickSource: OfferAnalyticsClickSource;
}

type PopupCallback =
	| (() => void)
	| ((id: number) => void)
	| ((type: string | number) => void)
	| ((id: number, type: string) => void)
	| ((newName: string) => void);

type PopupComponentPropValue =
	| string
	| number
	| boolean
	| ShopItem
	| ExtendedPiggyBankOffer
	| QuestActive
	| ChronicleData[]
	| SettlementCastleBuildingTileEntity
	| AbstractPoiTileEntity
	| PopupCallback
	| OpenedChest[][]
	| TileCoordinates;

export type PopupBodyData =
	| string
	| Partial<
			Record<
				string,
				| string
				| number
				| boolean
				| Record<string, string | number | null>
				| null
				| Date
				| { numOfPurchases: number }
				| { questId: number }
			>
	  >
	| News
	| MapExpansionConfig
	| null;

// NOTE: Actions and closeCallback removed from PopupState to prevent Immer destruction
export interface PopupState {
	id?: string | number | null;
	visible: boolean;
	type: PopupType;
	heading: string;
	body?: PopupBodyData;
	width?: number;
	height?: number | string | null;
	maxHeight?: number | null;
	skin?: SkinTypes;
	isPaddingless?: boolean;
	isCentered?: boolean;
	imgSrc?: string | null;
	hasOverlap?: boolean;
	hasOuterBar?: boolean;
	largeHeader?: boolean;
	closeable?: boolean;
	scaleDelay?: number;
	forceDividerLine?: boolean;
	spriteData?: Record<string, string | number | boolean> | null;
	showSkipMessageNotice?: boolean;
	decoratedHeading: string;
	percentageWidth: string;
	rewards?: Partial<Record<ResourceType, number>>;
	maxZIndex?: boolean;
	analytics?: OfferAnalytics | null;
	headingResource?: ResourceType | null;
}

const defaultState: PopupState = {
	id: null,
	visible: false,
	type: PopupType.INFO,
	heading: "",
	body: "",
	width: 22.125,
	height: null,
	maxHeight: null,
	skin: SkinTypes.LIGHT,
	isPaddingless: false,
	isCentered: false,
	imgSrc: null,
	hasOverlap: false,
	hasOuterBar: false,
	largeHeader: false,
	closeable: true,
	scaleDelay: 0,
	forceDividerLine: false,
	spriteData: null,
	showSkipMessageNotice: false,
	decoratedHeading: "",
	percentageWidth: "95%",
	rewards: undefined,
	maxZIndex: false,
	analytics: null,
	headingResource: null,
};

// Payload now includes all non-serializable functions and components
export interface PopupPayload extends Partial<PopupState> {
	id?: string | number | null;
	uniqueById?: boolean;
	component?: Component | null;
	componentProps?: Record<string, PopupComponentPropValue> | null | undefined;
	actions?: PopupAction[] | null;
	closeCallback?: () => void;
	isPriority?: boolean; // Flag for immediate display
	force?: boolean; // Bypasses duplicate protection
	/**
	 * If true, this popup is strictly ONE-time.
	 * If higher-priority popup interrupts it while this one is active,
	 * it will NOT be pushed to the queue >> it will be completely discarded,
	 * forcing the player to reopen it manually if they clicked 'X' or dismissed it.
	 */
	dropOnInterrupt?: boolean;
}

interface PrioritizedPopup extends PopupPayload {
	priority: number;
}

export enum PopupDictionary {
	INACTIVITY = "inactivity",
	CONNECTION_ERROR = "connection-error",
	PAYMENT_SUCCESSFUL = "payment-successful",
	THANK_YOU_VIDEO_AD = "thank-you-video-ad",
	COMPENSATION = "compensation",
	OFFLINE_EARNINGS = "offline-earnings",
	OFFLINE_MORE_AD = "offline-more-ad",
	LEVEL_UP = "level-up",
	DAILY_BONUS = "daily-bonus",
	NEWS = "news",
	EVENT = "event",
	EVENT_INTRO = "event-intro",
	EVENT_INFO = "event-info",
	EVENT_POSSIBLE_REWARDS = "event-possible-rewards",
	EVENT_CHEST = "event-chest",
	SPECIAL_CHEST_REWARD = "special-chest-reward",
	STORAGE = "storage",
	NOT_ENOUGH_STORAGE = "not-enough-storage",
	SEARCH_TOOL = "search-tool",
	CHANGE_NAME = "change-name",
	BUY_SPECIAL_BUILDING_PACK = "buy-special-building-pack",
	PURCHASE_SUCCESS = "purchase-success",
	UPGRADE_PRICE_SELECTION = "upgrade-price-selection",
	PREMIUM_UPGRADE = "premium-upgrade",
	RATE_US = "rate-us",
	RATE_OUR_GAME = "rate-our-game",
	QUICK_TOOL = "quick-tool",
	LOCKED_MAP_REGION = "locked-map-region",
	QUICK_TOOL_PREMIUM_BUILDING = "quick-tool-premium-building",
	ACCOUNT_LINKING = "account-linking",
	NOT_ENOUGH_RESOURCES = "not-enough-resources",
	GEM_CONFIRMATION = "gem-confirmation",
	LB_REWARDED_AD = "lb-rewarded-ad",
	PAYMENT_FAILED = "payment-failed",
	ABOUT_UPGRADES = "about-upgrades",
	INCOME_BOOST_AD = "income-boost-ad",
	CHRONICLE = "chronicle",
	RULES = "rules",
	DELETE_ACCOUNT = "delete-account",
	DISCORD_REWARD = "discord-reward",
	MONUMENT_REVEALED = "monument-revealed",
	PREMIUM_BUILDING = "premium-building",
	BRIBE = "bribe",
	SETTLEMENT_NAME = "settlement-name",
	SKIP_REQUIREMENTS = "skip-requirements",
	FOOD_INFO = "food-info",
	WELCOME_BACK = "welcome-back",
	WELCOME_BACK_REWARD = "welcome-back-reward",
	WELCOME_BACK_OFFER = "welcome-back-offer",
}

export const eventPopupIds = [
	PopupDictionary.EVENT,
	PopupDictionary.EVENT_INTRO,
	PopupDictionary.EVENT_INFO,
	PopupDictionary.EVENT_POSSIBLE_REWARDS,
	PopupDictionary.EVENT_CHEST,
];

// 1. Centralized priority mapping for popups (higher number means higher priority)
const POPUP_PRIORITIES: Partial<Record<PopupDictionary, number>> = {
	[PopupDictionary.INACTIVITY]: 100,
	[PopupDictionary.CONNECTION_ERROR]: 100,

	// Payment & claimable rewards
	[PopupDictionary.PAYMENT_SUCCESSFUL]: 95, // btn claimable
	[PopupDictionary.WELCOME_BACK]: 94,
	[PopupDictionary.WELCOME_BACK_REWARD]: 94,
	[PopupDictionary.THANK_YOU_VIDEO_AD]: 90, // auto-claimable (info-only)
	// "compensation" // RB clickable

	// Offline income and important ad rewards
	[PopupDictionary.OFFLINE_EARNINGS]: 80,
	[PopupDictionary.OFFLINE_MORE_AD]: 75,
	[PopupDictionary.LEVEL_UP]: 70,

	// Standard notifications and quests
	[PopupDictionary.DAILY_BONUS]: 55,
	[PopupDictionary.WELCOME_BACK_OFFER]: 54,
	[PopupDictionary.NEWS]: 50,
	[PopupDictionary.EVENT_INTRO]: 45,

	// Fallback for other unspecified IDs has a value of 0
};

const DEFAULT_PRIORITY = 0;

function getPopupPriority(popup: PopupPayload): number {
	// 1. Central dictionary definition always has the highest priority
	if (
		popup.id !== undefined &&
		popup.id !== null &&
		POPUP_PRIORITIES[popup.id as PopupDictionary] !== undefined
	) {
		return POPUP_PRIORITIES[popup.id as PopupDictionary]!;
	}

	// 2. Fallback to pre-calculated priority stored inside the object
	if (
		(popup as PrioritizedPopup).priority !== undefined &&
		(popup as PrioritizedPopup).priority !== null
	) {
		return (popup as PrioritizedPopup).priority;
	}

	// 3. Fallback for isPriority flag
	if (popup.isPriority) {
		return 90;
	}

	return DEFAULT_PRIORITY;
}

export const activePopupComponent = shallowRef<Component | null>(null);
export const activePopupProps = shallowRef<
	Record<string, PopupComponentPropValue> | null | undefined
>(null);
export const activeActions = shallowRef<PopupAction[] | null>(null);
export const activeCloseCallback = shallowRef<(() => void) | undefined>(
	undefined,
);

export interface PopupStore {
	state: Ref<PopupState>;
	isVisible: ComputedRef<boolean>;
	type: ComputedRef<PopupType>;
	heading: ComputedRef<string>;
	decoratedHeading: ComputedRef<string>;
	percentageWidth: ComputedRef<string>;
	body: ComputedRef<string | undefined>;
	width: ComputedRef<number | undefined>;
	height: ComputedRef<number | string | null | undefined>;
	maxHeight: ComputedRef<number | null | undefined>;
	skin: ComputedRef<SkinTypes | undefined>;
	isPaddingless: ComputedRef<boolean | undefined>;
	isCentered: ComputedRef<boolean | undefined>;
	imgSrc: ComputedRef<string | null | undefined>;
	hasOverlap: ComputedRef<boolean | undefined>;
	hasOuterBar: ComputedRef<boolean | undefined>;
	largeHeader: ComputedRef<boolean | undefined>;
	closeable: ComputedRef<boolean | undefined>;
	scaleDelay: ComputedRef<number | undefined>;
	forceDividerLine: ComputedRef<boolean | undefined>;
	spriteData: ComputedRef<
		Record<string, string | number | boolean> | null | undefined
	>;
	actions: ComputedRef<PopupAction[] | null>;
	showSkipMessageNotice: ComputedRef<boolean>;
	closeCallback: ComputedRef<(() => void) | undefined>;
	rewards: ComputedRef<Partial<Record<ResourceType, number>> | undefined>;
	maxZIndex: ComputedRef<boolean | undefined>;
	queueLength: ComputedRef<number>;
	headingResource: ComputedRef<ResourceType | null>;

	showPopup: (popup: PopupPayload) => void;
	updatePopup: (payload: Partial<PopupPayload>) => void;
	updateQueuedPopup: (
		identifier: {
			id?: string | number | null;
			type?: PopupType;
			heading?: string;
			decoratedHeading?: string;
		},
		payload: Partial<PopupPayload>,
	) => void;
	hidePopup: () => void;
	clearPopupQueue: () => void;
}

const STORE_NAME = "popup";

export const usePopupStore = defineStore(STORE_NAME, (): PopupStore => {
	const state = createImmutableState<PopupState>({ ...defaultState });

	const queue = shallowRef<PopupPayload[]>([]);
	let _isPopupActive = false;
	let _isHiding = false; // Security lock

	const isVisible = getImmutableStateProperty(state, "visible", false);
	const type = getImmutableStateProperty(state, "type", PopupType.INFO);
	const heading = getImmutableStateProperty(state, "heading", "");
	const decoratedHeading = getImmutableStateProperty(
		state,
		"decoratedHeading",
		"",
	);
	const body = getImmutableStateProperty(state, "body", "");
	const width = getImmutableStateProperty(state, "width", 22.125);
	const height = getImmutableStateProperty(state, "height", null);
	const maxHeight = getImmutableStateProperty(state, "maxHeight", null);
	const skin = getImmutableStateProperty(state, "skin", SkinTypes.LIGHT);
	const isPaddingless = getImmutableStateProperty(
		state,
		"isPaddingless",
		false,
	);
	const isCentered = getImmutableStateProperty(state, "isCentered", false);
	const imgSrc = getImmutableStateProperty(state, "imgSrc", null);
	const hasOverlap = getImmutableStateProperty(state, "hasOverlap", false);
	const scaleDelay = getImmutableStateProperty(state, "scaleDelay", 0);
	const forceDividerLine = getImmutableStateProperty(
		state,
		"forceDividerLine",
		false,
	);
	const hasOuterBar = getImmutableStateProperty(state, "hasOuterBar", false);
	const largeHeader = getImmutableStateProperty(state, "largeHeader", false);
	const closeable = getImmutableStateProperty(state, "closeable", true);
	const spriteData = getImmutableStateProperty(state, "spriteData", null);
	const showSkipMessageNotice = getImmutableStateProperty(
		state,
		"showSkipMessageNotice",
		false,
	);
	const percentageWidth = getImmutableStateProperty(
		state,
		"percentageWidth",
		"95%",
	);
	const rewards = getImmutableStateProperty(state, "rewards", undefined);
	const maxZIndex = getImmutableStateProperty(state, "maxZIndex", false);
	const headingResource = getImmutableStateProperty(
		state,
		"headingResource",
		null,
	);

	// Actions & closeCallback are now retrieved from local safe variables
	const actions = computed((): PopupAction[] | null => activeActions.value);
	const closeCallback = computed(
		(): (() => void) | undefined => activeCloseCallback.value,
	);

	const queueLength = computed((): number => queue.value.length);

	function _displayPopup(popup: PopupPayload): void {
		// Move functions/objects to safe storage
		activePopupComponent.value = popup.component
			? markRaw(popup.component)
			: null;
		activePopupProps.value = popup.componentProps
			? markRaw(popup.componentProps)
			: null;
		activeActions.value = popup.actions ?? null;
		activeCloseCallback.value = popup.closeCallback ?? undefined;

		const safePopupData = { ...popup };
		delete safePopupData.component;
		delete safePopupData.componentProps;
		delete safePopupData.actions;
		delete safePopupData.closeCallback;
		delete safePopupData.isPriority;
		delete safePopupData.uniqueById;

		const newState = {
			...defaultState,
			...safePopupData,
			visible: true,
			dropOnInterrupt: popup.dropOnInterrupt ?? false,
		};

		useSocketService().emitOfferAnalyticsSave({
			type: OfferAnalyticsType.OFFER_VIEW,
			clickSource: newState?.analytics?.clickSource ?? "UNKNOWN",
			storeId:
				(activePopupProps.value?.contentData as ShopItem)?.store_id ??
				"UNKNOWN",
			offerName:
				(activePopupProps.value?.contentData as ShopItem)?.name ??
				"UNKNOWN",
		});

		Object.keys(newState).forEach((key: string): void => {
			setImmutableStateProperty(
				state,
				key as keyof PopupState,
				newState[key as keyof PopupState],
			);
		});
	}

	// Instantly update current active popup
	function updatePopup(payload: Partial<PopupPayload>): void {
		const { component, componentProps, actions, closeCallback, ...rest } =
			payload;

		if (componentProps !== undefined) {
			activePopupProps.value = {
				...(activePopupProps.value || {}),
				...componentProps,
			};
		}
		if (component !== undefined) activePopupComponent.value = component;
		if (actions !== undefined) activeActions.value = actions;
		if (closeCallback !== undefined)
			activeCloseCallback.value = closeCallback;

		const statePayload = rest as Partial<PopupState>;

		Object.keys(statePayload).forEach((key: string): void => {
			setImmutableStateProperty(
				state,
				key as keyof PopupState,
				statePayload[key as keyof PopupState],
			);
		});
	}

	// Update queued popup by identifiers
	function updateQueuedPopup(
		identifier: {
			id?: string | number | null;
			type?: PopupType;
			heading?: string;
			decoratedHeading?: string;
		},
		payload: Partial<PopupPayload>,
	): void {
		// Popup is located within the queue using provided identifiers
		const targetIndex = queue.value.findIndex(
			(p: PopupPayload): boolean => {
				if (identifier.id && p.id !== identifier.id) return false;
				if (identifier.type && p.type !== identifier.type) return false;
				if (identifier.heading && p.heading !== identifier.heading)
					return false;
				if (
					identifier.decoratedHeading &&
					p.decoratedHeading !== identifier.decoratedHeading
				)
					return false;
				return true;
			},
		);

		if (targetIndex === -1) return;

		const nextQueue = [...queue.value];
		const targetPopup = nextQueue[targetIndex];

		// Old and new componentProps are merged safely.
		const mergedProps = {
			...(typeof targetPopup.componentProps === "object"
				? targetPopup.componentProps
				: {}),
			...(typeof payload.componentProps === "object"
				? payload.componentProps
				: {}),
		};

		// Existing state properties inside the queue item are updated based on the payload
		const updatedState = { ...targetPopup };
		(Object.keys(payload) as Array<keyof PopupPayload>).forEach(
			(key: keyof PopupPayload): void => {
				const value = payload[key];
				if (value !== undefined) {
					(updatedState as Record<keyof PopupPayload, unknown>)[key] =
						value;
				}
			},
		);

		// Snapshot in the queue is updated
		nextQueue[targetIndex] = {
			...updatedState,
			componentProps:
				Object.keys(mergedProps).length > 0 ? mergedProps : null,
		};

		queue.value = nextQueue;
	}

	function _clearPopup(): void {
		activePopupComponent.value = null;
		activePopupProps.value = null;
		activeActions.value = null;
		activeCloseCallback.value = undefined;

		Object.keys(defaultState).forEach((key: string): void => {
			setImmutableStateProperty(
				state,
				key as keyof PopupState,
				defaultState[key as keyof PopupState],
			);
		});
	}

	function showPopup(popup: PopupPayload): void {
		const popupPriority = getPopupPriority(popup);
		const popupWithPriority = { ...popup, priority: popupPriority };

		// 1. Deduplication control by ID (Upsert / Queue update)
		if (popup.id !== undefined && popup.id !== null) {
			const existingIndex = queue.value.findIndex(
				(p: PopupPayload): boolean => p.id === popup.id,
			);

			if (existingIndex !== -1) {
				// If already waiting in the queue, update its data and re-sort position for correct priority
				const nextQueue = [...queue.value];
				nextQueue[existingIndex] = {
					...nextQueue[existingIndex],
					...popupWithPriority,
					componentProps: {
						...(typeof nextQueue[existingIndex].componentProps ===
						"object"
							? nextQueue[existingIndex].componentProps
							: {}),
						...(typeof popup.componentProps === "object"
							? popup.componentProps
							: {}),
					},
				};
				// Re-sort queue based on priority map
				nextQueue.sort(
					(a: PopupPayload, b: PopupPayload): number =>
						getPopupPriority(b) - getPopupPriority(a),
				);
				queue.value = nextQueue;
				return;
			}

			// If the identical popup is currently active on screen
			if (_isPopupActive && state.value.id === popup.id) {
				updatePopup(popup);
				return;
			}
		}

		if (_isPopupActive) {
			// Protection against duplicates with the active window
			const isIdenticalToActive =
				!popup.force &&
				state.value.heading === popup.heading &&
				state.value.type === (popup.type ?? PopupType.INFO) &&
				activePopupComponent.value === (popup.component ?? null);

			if (isIdenticalToActive) {
				return;
			}

			// Get the priority of the currently active popup on screen
			const currentActivePriority = getPopupPriority({
				id: state.value.id,
			});

			// 2. If the new popup has strictly higher priority than the active one
			// (or has an explicit isPriority flag), interrupt it immediately.
			if (popup.isPriority || popupPriority > currentActivePriority) {
				// Check if the currently active popup should be dropped upon interruption
				const activePopupDropOnInterrupt = !!(
					state.value as unknown as PopupPayload
				)?.dropOnInterrupt;

				// If it doesn't have dropOnInterrupt, push it to the queue safely (standard behavior)
				if (!activePopupDropOnInterrupt) {
					const currentPopupSnapshot: PopupPayload = {
						...state.value,
						id: state.value.id, // Ensure ID is safely retained in snapshot
						component: activePopupComponent.value,
						isPriority:
							(state.value as unknown as PopupPayload)
								.isPriority ?? popup.isPriority,
						componentProps: activePopupProps.value,
						actions: activeActions.value,
						closeCallback: activeCloseCallback.value,
						dropOnInterrupt: false,
					};

					// Push current popup back to the queue and re-sort entire queue
					const nextQueue = [...queue.value, currentPopupSnapshot];
					nextQueue.sort(
						(a: PopupPayload, b: PopupPayload): number =>
							getPopupPriority(b) - getPopupPriority(a),
					);
					queue.value = nextQueue;

					// eslint-disable-next-line no-console
					console.log("queue:", queue.value); // TODO DS testing
				}

				// If dropOnInterrupt is true, the current popup skips the queue and is completely discarded.

				// Display the new higher-priority popup instantly
				_displayPopup(popupWithPriority);
				return;
			}

			// 3. Otherwise, push it to the sorted queue normally
			const nextQueue = [...queue.value, popupWithPriority];

			// Sort queue descending by priority (highest priority ends up at queue.value[0])
			nextQueue.sort(
				(a: PopupPayload, b: PopupPayload): number =>
					getPopupPriority(b) - getPopupPriority(a),
			);

			queue.value = nextQueue;

			// eslint-disable-next-line no-console
			console.log("queue:", queue.value); // TODO DS testing
		} else {
			_isPopupActive = true;
			_displayPopup(popupWithPriority);
		}
	}

	function hidePopup(): void {
		if (_isHiding) return;
		_isHiding = true;

		try {
			// The callback is retrieved from the shallowRef, preventing its destruction by Immer
			const currentCloseCallback = activeCloseCallback.value;

			if (currentCloseCallback) {
				activeCloseCallback.value = undefined;
				try {
					currentCloseCallback();
				} catch (error: unknown) {
					console.error("Error in popup closeCallback:", error);
				}
			}

			if (queue.value.length > 0) {
				const nextQueue = [...queue.value];
				const nextPopup = nextQueue.shift()!;
				queue.value = nextQueue;

				_displayPopup(nextPopup);
				return;
			}

			_isPopupActive = false;
			_clearPopup();
		} finally {
			// The security lock is released under all circumstances
			_isHiding = false;
		}
	}

	function clearPopupQueue(): void {
		queue.value = [];
		_isPopupActive = false;
	}

	return {
		state,
		isVisible,
		type,
		heading,
		decoratedHeading,
		percentageWidth,
		body,
		width,
		height,
		maxHeight,
		skin,
		isPaddingless,
		isCentered,
		imgSrc,
		hasOverlap,
		hasOuterBar,
		largeHeader,
		closeable,
		scaleDelay,
		forceDividerLine,
		spriteData,
		actions,
		showSkipMessageNotice,
		closeCallback,
		rewards,
		maxZIndex,
		queueLength,
		headingResource,

		showPopup,
		updatePopup,
		updateQueuedPopup,
		hidePopup,
		clearPopupQueue,
	};
});
